import React, { useEffect, useRef, useState } from "react";
import { Search, Bell, Globe2, ChevronRight, MessageCircle, Users, Check } from "lucide-react";
import { LegalMenu } from "./LegalExperience";
import { searchProfiles } from "../lib/dmApi";
import { useMiniProfile } from "../lib/miniProfileContext";

export function GlobalUserSearch() {
  const { open: openMiniProfile } = useMiniProfile();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const items = await searchProfiles(term);
        if (!cancelled) setResults(items);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setFocused(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    return () => document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, []);

  const showResults = focused && query.trim().length >= 2;

  return (
    <div ref={rootRef} className="pointer-events-auto w-full relative">
      <Search size={15} className="absolute left-2.5 sm:left-4 top-1/2 -translate-y-1/2 text-muted" />
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onFocus={() => setFocused(true)}
        placeholder="Cerca utenti"
        aria-label="Cerca utenti"
        autoComplete="off"
        data-testid="search-input"
        className="w-full min-w-0 pl-8 sm:pl-11 pr-2 sm:pr-4 py-2 sm:py-2.5 bg-white border border-edge rounded-full text-xs sm:text-sm outline-none focus:border-ink focus:ring-2 focus:ring-ink/10"
      />
      {showResults && (
        <div
          className="absolute left-0 right-auto top-[calc(100%+8px)] z-[100] w-[min(320px,calc(100vw-16px))] max-h-80 overflow-y-auto rounded-2xl border border-edge bg-white p-2 shadow-xl"
          data-testid="global-search-results"
        >
          {loading && <div className="px-3 py-3 text-xs text-muted">Ricerca…</div>}
          {!loading && results.length === 0 && (
            <div className="px-3 py-3 text-xs text-muted">Nessun utente trovato</div>
          )}
          {!loading && results.map((profile) => (
            <button
              key={profile.identityId}
              type="button"
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left hover:bg-cream"
              onClick={() => {
                setFocused(false);
                setQuery("");
                void openMiniProfile(profile.identityId);
              }}
              data-testid={`global-search-result-${profile.identityId}`}
            >
              <img
                src={profile.avatar || "/you-voice-logo.png"}
                alt=""
                className="h-9 w-9 shrink-0 rounded-full border border-edge object-cover"
              />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-ink">{profile.displayName}</span>
                <span className="block truncate font-mono text-[10px] text-muted">@{profile.identityId.slice(0, 8)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileGlobalSearch() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutside);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative z-[130] shrink-0 sm:hidden" data-testid="mobile-global-search">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={`w-8 h-8 flex items-center justify-center rounded-full text-muted hover:text-ink hover:bg-white ${open ? "bg-white text-primary ring-2 ring-primary/20" : ""}`}
        aria-label="Cerca utenti"
        aria-expanded={open}
        data-testid="mobile-global-search-toggle"
      >
        <Search size={19} />
      </button>
      {open && (
        <div className="absolute right-0 top-[calc(100%+10px)] z-[140] w-[min(280px,calc(100vw-16px))] rounded-2xl border border-edge bg-cream p-2 shadow-2xl" data-testid="mobile-global-search-panel">
          <GlobalUserSearch />
        </div>
      )}
    </div>
  );
}

const APP_LANGUAGES = [
  ["it", "Italiano"],
  ["en", "English"],
  ["es", "Español"],
  ["fr", "Français"],
  ["de", "Deutsch"],
  ["pt", "Português"],
];
const LANGUAGE_STORAGE_KEY = "ol-preferred-language";

function deviceLanguage() {
  const supported = APP_LANGUAGES.map(([code]) => code);
  const candidates = [...(navigator.languages || []), navigator.language];
  return candidates
    .map((value) => String(value || "").toLowerCase().split(/[-_]/)[0])
    .find((code) => supported.includes(code)) || "it";
}

function storedLanguage() {
  try {
    const value = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (APP_LANGUAGES.some(([code]) => code === value)) return value;
    const detected = deviceLanguage();
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, detected);
    return detected;
  } catch {
    return deviceLanguage();
  }
}

function LanguageMenu() {
  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState(storedLanguage);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutside);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const selectLanguage = (code) => {
    setLanguage(code);
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
    } catch {}
    window.dispatchEvent(new CustomEvent("ol-language-change", { detail: { language: code } }));
    setOpen(false);
  };

  const activeLabel = APP_LANGUAGES.find(([code]) => code === language)?.[1] || "Italiano";

  return (
    <div ref={rootRef} className="relative z-20 block shrink-0" data-testid="language-menu-root">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={`relative z-10 w-9 h-9 flex items-center justify-center rounded-full text-muted hover:text-ink hover:bg-white ${open ? "bg-white text-primary ring-2 ring-primary/20" : ""}`}
        data-testid="lang-btn"
        aria-label="Scegli lingua"
        aria-expanded={open}
        title={`Lingua: ${activeLabel}`}
      >
        <Globe2 size={20} />
      </button>
      {open && (
        <section
          className="absolute left-0 top-[calc(100%+10px)] z-[120] w-64 overflow-hidden rounded-2xl border-2 border-ink bg-cream shadow-2xl"
          role="menu"
          aria-label="Seleziona lingua"
          data-testid="language-menu"
        >
          <header className="border-b border-edge bg-white px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary">You Voice</p>
            <h2 className="text-sm font-bold text-ink">Lingua delle traduzioni</h2>
            <p className="mt-0.5 text-[11px] text-muted">La scelta resta salvata su questo dispositivo.</p>
          </header>
          <div className="p-2">
            {APP_LANGUAGES.map(([code, label]) => {
              const selected = code === language;
              return (
                <button
                  key={code}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selected}
                  onClick={() => selectLanguage(code)}
                  className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition-colors ${selected ? "bg-primary text-white" : "text-ink hover:bg-white"}`}
                  data-testid={`language-option-${code}`}
                >
                  <span>{label}</span>
                  {selected && <Check size={17} strokeWidth={3} />}
                </button>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

export function Header({ notifCount = 0, user, onOpenProfile, onGoHome, onOpenMessages, onOpenNotifications, onOpenNetwork, onOpenLegal, onOpenAdmin, unreadDm = 0, showSearch = true, showMobileSearch = showSearch, showLegalMenu = true }) {
  return (
    <header className="isolate border-b border-edge bg-cream sticky top-0 z-[90] shrink-0">
      <div className="max-w-[1600px] mx-auto px-2 sm:px-6 h-14 sm:h-16 flex items-center gap-0.5 sm:gap-4 overflow-visible">
        <button onClick={onGoHome} className="relative z-10 flex items-center gap-2 shrink-0" data-testid="brand-logo">
          <img
            src="/you-voice-brand.png?v=20260811"
            alt="You Voice"
            className="brand-wordmark object-contain"
          />
        </button>
        <LanguageMenu />
        {showMobileSearch && <MobileGlobalSearch />}
        <button
          onClick={() => onOpenMessages && onOpenMessages()}
          className="relative z-10 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-muted hover:text-ink shrink-0"
          data-testid="messages-btn"
          aria-label="Messaggi"
          title="Messaggi privati (cifrati E2E)"
        >
          <MessageCircle size={20} />
          {unreadDm > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center"
              data-testid="messages-unread-badge"
            >
              {unreadDm > 9 ? "9+" : unreadDm}
            </span>
          )}
        </button>
        <button
          onClick={() => onOpenNetwork && onOpenNetwork()}
          className="relative z-10 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-muted hover:text-ink shrink-0"
          data-testid="network-btn"
          aria-label="La mia rete"
          title="La mia rete"
        >
          <Users size={20} />
        </button>
        <button
          onClick={() => onOpenNotifications && onOpenNotifications()}
          className="relative z-10 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-muted hover:text-ink shrink-0"
          data-testid="notif-btn"
          aria-label="Notifiche"
          title="Notifiche"
        >
          <Bell size={20} />
          {notifCount > 0 && (
            <span
              className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center"
              data-testid="notif-badge"
            >
              {notifCount > 9 ? "9+" : notifCount}
            </span>
          )}
        </button>
        {showLegalMenu && onOpenLegal && <LegalMenu onOpen={onOpenLegal} onOpenAdmin={onOpenAdmin} onOpenTopics={onGoHome} />}
        <button onClick={onOpenProfile} className="relative z-10 flex items-center gap-2 pl-1 sm:pl-2 border-l border-edge hover:opacity-80 shrink-0" data-testid="profile-badge">
          <img
            src={user?.avatar || "/you-voice-logo.png"}
            alt={user?.name || "Profilo"}
            className="w-8 h-8 sm:w-9 sm:h-9 rounded-full object-cover border border-edge"
          />
          <span className="font-semibold text-sm text-ink hidden md:inline">{user?.name || "Profilo"}</span>
          <ChevronRight size={14} className="rotate-90 text-muted hidden sm:inline" />
        </button>
        {showSearch && (
          <div
            className="pointer-events-none hidden sm:block w-[104px] min-w-[104px] flex-none sm:min-w-0 sm:w-auto sm:flex-1 max-w-[180px] sm:max-w-[360px] md:max-w-none md:ml-auto"
            data-testid="global-search"
          >
            <GlobalUserSearch />
          </div>
        )}
      </div>
    </header>
  );
}
